import React from 'react';
import TopContentFreeShipping from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentFreeShipping';
import TopPageCMSBannerMessage from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopPageCMSBannerMessage';
import TopContentBBQMessage from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentBBQMessage';
import TopContentMessage from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentMessage';
import TopContentBirthdayMessage from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentBirthdayMessage';
import TopContentCanadaPostStrikeMessage from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/TopContentCanadaPostStrikeMessage';
import RewardBundlingAwarenessMessage from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/RewardBundlingAwarenessMessage';
import AssuranceZone from 'components/AssuranceZone';
import localeUtils from 'utils/LanguageLocale';
import { getQuickLookDeeplinkParams, openQuickLookDeeplink } from 'utils/quickLookDeeplink';
import * as RwdBasketConst from 'constants/RwdBasket';
import constants from 'constants/content';
import TopContentFreeReturnsEN from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/locales/TopContentFreeReturns_en_US';
import TopContentFreeReturnsFR from 'components/RwdBasket/RwdBasketLayout/TopContentMessages/locales/TopContentFreeReturns_fr_CA';
import { isRewardBundlingEnabled } from 'utils/BeautyInsider';

const {
    MAIN_BASKET_TYPES: { DC_BASKET, BOPIS_BASKET },
    TOP_BANNER_MESSAGES: { FREE_RETURNS, TIER, CANADA_POST_STRIKE },
    TOP_BANNER_PERSONALIZED_MESSAGES
} = RwdBasketConst;
const getText = localeUtils.getLocalisedDataFromResource(TopContentFreeReturnsEN, TopContentFreeReturnsFR);

const {
    COMPONENT_TYPES: { BANNER }
} = constants;

// Resolves the action-related props for a CMS banner. A Quick Look deeplink
// (OMNX-11286: action targetUrl carries `quicklook=true`) yields an onClick that
// opens Quick Look in place and suppresses navigation; any other action yields a
// plain targetUrl. Returning one or the other (never both) keeps
// TopPageCMSBannerMessage's navigation behavior unambiguous.
function getBannerAction(action) {
    const quickLookDeeplink = getQuickLookDeeplinkParams(action);

    if (quickLookDeeplink) {
        return {
            onClick: e => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                openQuickLookDeeplink(quickLookDeeplink);
            }
        };
    }

    return { targetUrl: action?.targetUrl };
}

function getCMSComponentList(content) {
    return content.reduce((acc, obj) => {
        if (obj.sid === TOP_BANNER_PERSONALIZED_MESSAGES.BIRTHDAY_GIFT) {
            acc.push(
                <TopContentBirthdayMessage
                    {...obj}
                    key={obj.sid}
                />
            );

            return acc;
        }

        if (obj.sid === CANADA_POST_STRIKE) {
            acc.push(
                <TopContentCanadaPostStrikeMessage
                    {...obj}
                    icon={obj.media?.src}
                    key={obj.sid}
                />
            );

            return acc;
        }

        if (obj.type === BANNER && obj.text) {
            acc.push(
                <TopPageCMSBannerMessage
                    key={obj.sid}
                    text={obj.text}
                    icon={obj.media?.src}
                    {...getBannerAction(obj.action)}
                />
            );
        }

        return acc;
    }, []);
}

// INFL-4194 / INFL-4195 stipulated ordering
function getOrderedTopContentComponentList({
    isSignedIn,
    isBIUser,
    basketLevelMessages = [],
    realTimeVIBMessages,
    cmsZone,
    potentialBeautyBankPoints = 0,
    showBasketShippingAndPoints = false,
    showFreeReturnsOnBasket = false
}) {
    const out = [];

    if ((isSignedIn && !isBIUser) || !isSignedIn) {
        out.push(
            <TopContentFreeShipping
                isBIUser={isBIUser}
                isSignedIn={isSignedIn}
                potentialBeautyBankPoints={potentialBeautyBankPoints}
                showBasketShippingAndPoints={showBasketShippingAndPoints}
            />
        );
    }

    out.push(...getCMSComponentList(cmsZone));

    const bbqMessage = basketLevelMessages.find(message => message.messageContext.includes('basket.promotion'));

    if (bbqMessage) {
        out.push(<TopContentBBQMessage {...bbqMessage} />);
    }

    const tierMessage = realTimeVIBMessages?.[0];

    if (tierMessage) {
        out.push(
            <TopContentMessage
                messageKey={TIER}
                icon='bell'
                message={tierMessage}
            />
        );
    }

    if (showFreeReturnsOnBasket) {
        out.push(
            <AssuranceZone
                key={FREE_RETURNS}
                dataAt={Sephora.debug.dataAt('basket_assurance_zone')}
            />
        );
    } else {
        out.push(
            <TopContentMessage
                messageKey={FREE_RETURNS}
                icon='box'
                message={getText('freeReturns')}
            />
        );
    }

    return out;
}

function getTopContentMessageComponentList({
    basket: {
        basketLevelMessages, realTimeVIBMessages, pickupBasket, potentialBeautyBankPoints = 0, items: dcItems, appliedPromotions
    },
    user: { isSignedIn, isBIUser },
    cmsData: { topContent = [], bopisTopContent = [] } = {},
    showBasketShippingAndPoints,
    showFreeReturnsOnBasket
}) {
    const saDMessages = getOrderedTopContentComponentList({
        basketLevelMessages,
        realTimeVIBMessages,
        isSignedIn,
        isBIUser,
        cmsZone: topContent,
        potentialBeautyBankPoints,
        showBasketShippingAndPoints,
        showFreeReturnsOnBasket
    });

    const bopisMessages = getOrderedTopContentComponentList({
        basketLevelMessages: pickupBasket?.basketLevelMessages,
        realTimeVIBMessages: pickupBasket?.realTimeVIBMessages,
        isSignedIn,
        isBIUser,
        cmsZone: bopisTopContent,
        showFreeReturnsOnBasket
    });

    const isRewardBundlingMessageEnabled = isRewardBundlingEnabled();

    saDMessages.unshift(
        <RewardBundlingAwarenessMessage
            key='reward-bundling-awareness'
            isSignedIn={isSignedIn}
            isEnabled={isRewardBundlingMessageEnabled}
            items={dcItems}
            basketRoot={{ appliedPromotions, items: dcItems }}
            isBopisBasket={false}
        />
    );

    bopisMessages.unshift(
        <RewardBundlingAwarenessMessage
            key='reward-bundling-awareness-bopis'
            isSignedIn={isSignedIn}
            isEnabled={isRewardBundlingMessageEnabled}
            items={pickupBasket?.items}
            basketRoot={pickupBasket}
            isBopisBasket={true}
        />
    );

    return {
        [DC_BASKET]: {
            isAvailable: saDMessages.length > 0,
            messages: saDMessages
        },
        [BOPIS_BASKET]: {
            isAvailable: bopisMessages.length > 0,
            messages: bopisMessages
        }
    };
}

export { getTopContentMessageComponentList };
